import { useState } from 'react';

import { fmtRangeLabel, PRESETS } from '../lib/dateRange';
import { useDateRange } from '../lib/useDateRange';
import { Input } from './Input';

const chipCls = (active: boolean) =>
  `min-h-11 px-3.5 rounded-chip border text-sm-label font-semibold transition-colors ${
    active
      ? 'bg-primary border-primary text-white'
      : 'bg-surface border-line text-ink-2 hover:border-primary hover:text-primary'
  }`;

/** Global report date range: preset chips + a custom-range popover. URL-backed (see useDateRange). */
export function DateRangeFilter() {
  const {
    preset,
    range,
    prev,
    setPreset,
    setCustom,
    customFromIso,
    customToIso,
  } = useDateRange();
  const [open, setOpen] = useState(false);

  return (
    <section
      aria-label="ตัวกรองช่วงเวลารายงาน"
      className="bo-card mb-6 grid min-w-0 gap-3 p-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center lg:grid-cols-[auto_minmax(0,1fr)_auto]"
    >
      <span className="text-sm-label font-bold text-ink-3">ช่วงเวลา</span>
      <div className="grid min-w-0 grid-cols-2 gap-2 min-[420px]:grid-cols-3 sm:flex sm:flex-wrap sm:gap-1.5">
        {PRESETS.map((p) =>
          p.key === 'custom' ? (
            <div key="custom" className="relative min-w-0">
              <button
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="dialog"
                aria-expanded={open}
                className={`${chipCls(preset === 'custom')} w-full sm:w-auto`}
              >
                {p.label}…
              </button>
              {open && (
                <>
                  <div
                    className="fixed inset-0 z-0 bg-black/35 backdrop-blur-[1px] sm:bg-transparent sm:backdrop-blur-none"
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                  />
                  <div
                    role="dialog"
                    aria-label="เลือกช่วงวันที่"
                    className="fixed inset-x-4 top-1/2 z-10 flex -translate-y-1/2 flex-col gap-4 bo-card p-4 shadow-modal sm:absolute sm:inset-x-auto sm:top-auto sm:left-0 sm:mt-1.5 sm:w-64 sm:translate-y-0"
                  >
                    <div className="flex items-start justify-between gap-4 border-b border-line-2 pb-3">
                      <div>
                        <div className="font-bold text-ink">
                          กำหนดช่วงวันที่
                        </div>
                        <div className="mt-1 text-sm-label text-ink-3">
                          เลือกวันเริ่มต้นและสิ้นสุด
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label="ปิดตัวเลือกช่วงวันที่"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink-3 hover:bg-bg-warm hover:text-ink focus:outline-none focus:ring-2 focus:ring-primary/30"
                        onClick={() => setOpen(false)}
                      >
                        ✕
                      </button>
                    </div>
                    <label className="flex flex-col gap-1.5 text-sm-label font-semibold text-ink-2">
                      จากวันที่
                      <Input
                        type="date"
                        defaultValue={customFromIso}
                        max={customToIso}
                        onChange={(e) =>
                          e.target.value &&
                          setCustom(e.target.value, customToIso)
                        }
                      />
                    </label>
                    <label className="flex flex-col gap-1.5 text-sm-label font-semibold text-ink-2">
                      ถึงวันที่
                      <Input
                        type="date"
                        defaultValue={customToIso}
                        min={customFromIso}
                        onChange={(e) =>
                          e.target.value &&
                          setCustom(customFromIso, e.target.value)
                        }
                      />
                    </label>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`${chipCls(preset === p.key)} w-full sm:w-auto`}
            >
              {p.label}
            </button>
          ),
        )}
      </div>
      <div className="min-w-0 text-sm-label leading-5 text-ink-3 sm:col-span-2 lg:col-span-1 lg:text-right">
        แสดง <b className="text-ink font-semibold">{fmtRangeLabel(range)}</b>
        <span className="mx-1.5 text-ink-4">·</span>
        เทียบ <b className="text-ink-2 font-medium">{fmtRangeLabel(prev)}</b>
      </div>
    </section>
  );
}
